import {
  BookingItemLight,
  Cart,
  CartEvent,
  CartTotal,
  CartTotalSurcharge,
  DeliveryType,
  LegacyTicket,
  PriceInfoEnum,
  PriceType,
  SurchargeType,
  TicketIds,
  TicketPrice,
  TicketPriceType,
  UnassignedTicket
} from '../../generated/sales-terminal-client';
import {EventEmitter} from '@angular/core';
import {AuthService} from '../auth/auth.service';
import {LanguageService} from '../language/language.service';
import {SnackService} from '../snack/snack.service';
import {StorageService} from '../storage/storage.service';
import {PriceInCentsPipe} from '../../pipes/priceInCents/priceInCents.pipe';
import {TicketPricePipe} from '../../pipes/ticketPrice/ticketPrice.pipe';
import {PricePipe} from '../../pipes/price/price.pipe';
import {Observable} from 'rxjs';
import {CustomTicket} from '../../modules/sales-terminal/cart/table/cart-table.component';
import {CartInterface} from './cart.interface';
import {cloneDeep, sumBy} from 'lodash-es';
import {EventService} from '../event/event.service';
import {GroupTickets} from '../../modules/sales-terminal/cart/cart.constants';
import {CartService} from './cart.service';
import {RouteService} from '../route/route.service';
import {SelfServiceEventService} from '../self-service/event/self-service-event.service';
import {Privileges} from "../../modules/admin/privileges/privileges.constants";
import {CentrePropertyService} from "../centre-property/centre-property.service";

export interface CartDraftEvent {
  id: number;
  isDraft: boolean;
  boEvent: boolean;
}
export interface CartSortedEvent extends CartEvent {
  legacyZones?: CartSectorGroup[];
  legacyRows?: CartRowGroup[];
  legacyTypes?: CartGroup[];
  availablePriceMap: Record<string, PriceType[]>;
  quantity: number;
  total: number;
  surcharges: CartTotalSurcharge[];
  legacyTickets: CustomTicket[];
}
export interface CartSectorGroup extends CartGroup {
  typeId: number;
  typeName: string;
}
export interface CartRowGroup extends CartGroup {
  row: string;
}
export interface CartGroup {
  id?: number;
  name: string;
  quantity: number;
  tickets: CustomTicket[];
}

export type ChildSelectionState = 'none' | 'some' | 'all';
export const ChildSelectionState = {
  NONE: 'none' as ChildSelectionState,
  SOME: 'some' as ChildSelectionState,
  ALL: 'all' as ChildSelectionState
}

export type CartType = 'selfService' | 'legacy';
export const CartTypes = {
  SelfService: 'selfService' as CartType,
  Legacy: 'legacy' as CartType,
}

export interface SelectionMap {
  checked?: ChildSelectionState;
  events?: Record<string, SelectionMapEvent>;
}
export interface SelectionMapGroup {
  checked?: ChildSelectionState;
  tickets?: Record<string, ChildSelectionState>;
}
export interface SelectionMapEvent extends SelectionMapGroup {
  priceZones?: Record<string, SelectionMapGroup>;
  priceRows?: Record<string, SelectionMapGroup>;
  priceTypes?: Record<string, SelectionMapGroup>;
  ticketTypes?: Record<string, ChildSelectionState>;
}

export abstract class CartBaseService implements CartInterface<any> {
  public clientNotificationEmail: string;
  public priceInfo: PriceInfoEnum;
  public ticketTypeChanges: Record<string, number> = {};
  public priceTypeChanges: Record<string, any> = {};
  public currentBooking: BookingItemLight;
  public customPriceRequest: TicketPrice[] = [];
  public priceTypeRequest: TicketPriceType[] = [];
  public ticketEventMap: CartSortedEvent[] = [];
  public eventTicketIdMap: Record<string, number[]> = {};
  public cartEventIds: number[] = [];
  public selectionMap: SelectionMap = {
    checked: ChildSelectionState.ALL,
    events: {}
  };
  public ticketCount: number;
  public cartType: CartType;
  public cartDraftEvent: CartDraftEvent;

  public cartFees: CartTotalSurcharge[] = [];
  public cartHiddenFees: CartTotalSurcharge[] = [];
  public cartFeesCompact: CartTotalSurcharge = null;
  public cartSalespointFees: CartTotalSurcharge[] = [];
  public cartTotalPrice = 0;
  public cartSubTotal: number;
  public hasSelectedSeasonTickets = false;
  public caller = false; // Used to determine origin of caller between customer and cashier screen
  public markForCheck: EventEmitter<any> = new EventEmitter();
  public groupPriceTypeMap: Record<string, number> = {};
  public groupCustomPriceMap: Record<string, string> = {};

  public totalScrapSum: number;

  public get canPrintWithoutPrice(): boolean {
    return this.authService.checkPrivileges(Privileges.TICKET_WITHOUT_PRICE_PRINT) && this.hasSelectedTickets;
  }

  protected _emptyTotals: CartTotal = {
    surcharges: [],
    insurances: [],
    ticketTotal: 0,
    total: 0,
    ticketQuantity: 0,
    giftCardCreditTotal: 0
  };
  protected _emptyCart: Cart = {
    events: [],
    giftCards: [],
    deliveryConfiguration: null,
    legacyPromocode: null,
    discountPercent: 0,
    total: this._emptyTotals
  };
  protected _cart: Cart;
  public get cart(): Cart {
    return this._cart || this._emptyCart;
  }
  public set cart(newCart: Cart) {
    this._cart = newCart;
    this.cartTotal = newCart.total;
    this.mapTickets(this._cart.events);
    this.updateSelectionMap();
  }

  protected _isLoading = false;
  public get isLoading(): boolean {
    return this._isLoading;
  }
  public set isLoading(value: boolean) {
    this._isLoading = value;
    this.nextMarkForCheck();
  }

  protected _groupTicketsBy = GroupTickets.None;
  public get groupTicketsBy(): GroupTickets {
    return this._groupTicketsBy;
  }
  public set groupTicketsBy(value: GroupTickets) {
    this._groupTicketsBy = value;
    this.nextMarkForCheck();
  }

  public get isSelfService(): boolean {
    return this.cartType === CartTypes.SelfService;
  }

  public nextMarkForCheck(): void {
    this.markForCheck.next(new Date().valueOf());
  }

  protected constructor(protected authService: AuthService,
                        protected centrePropertyService: CentrePropertyService,
                        protected languageService: LanguageService,
                        protected snackService: SnackService,
                        protected storageService: StorageService,
                        protected eventService: EventService,
                        protected selfServiceEventService: SelfServiceEventService,
                        protected routeService: RouteService,
                        protected priceInCentsPipe: PriceInCentsPipe,
                        protected ticketPricePipe: TicketPricePipe,
                        protected pricePipe: PricePipe) {}

  abstract retryCart(): void;
  abstract subTotals(ticketIds: TicketIds, bookingId?: number): Observable<CartTotal>;
  abstract updateSubTotals(preserveSelected?: boolean, preserveGifts?: boolean): void;

  public get events(): CartEvent[] {
    return this.cart?.events || [];
  }

  public selectAllTickets(): void {
    this.populateSelectionMap();
  }
  public updateSelectionMap(updateSubTotals?: boolean): void {
    this.populateSelectionMap(ChildSelectionState.ALL, true, updateSubTotals);
  }

  public updateCurrentBooking(newBooking: BookingItemLight): void {
    this.currentBooking = newBooking;
  }

  public getEventTicketIds(eventId: number): number[] {
    return this.eventTicketIdMap[eventId] || [];
  }

  public get ticketQuantity(): number { // ticketAmount
    return this.cart?.total?.ticketQuantity || 0;
  }
  public get totalSum(): number { // cartTotalSum
    return this.cart?.total?.total || 0;
  }

  public get cartTotal(): CartTotal {
    return this.cart?.total || this._emptyTotals;
  }
  public set cartTotal(newTotal: CartTotal) {
    this.cart.total = newTotal;
    this.cartHiddenFees = [];
    this.cartFees = [];
    const spFees: CartTotalSurcharge[] = [];
    let spFeeTotal: number = 0;
    let feeTotal: number = 0;
    let displayServiceFeeWithZeroPrice = false;

    this.cartSurcharges.forEach((totalSurcharge: CartTotalSurcharge) => {
      if (totalSurcharge.name.some(t => t.value === 'general.api_fees_salespoint')) {
        spFees.push(totalSurcharge);
        spFeeTotal += totalSurcharge.total;
      } else {
        if (totalSurcharge.type === SurchargeType.Hidden ||
          (this.centrePropertyService.isEnabled('exclude_own_fees') && totalSurcharge.ownerSuperCentreId !== this.authService.user.superCentreId &&
            totalSurcharge.type !== SurchargeType.Service)) {
          this.cartHiddenFees.push(totalSurcharge);
        } else {
          this.cartFees.push(totalSurcharge);
          if (this.isSelfService && totalSurcharge.type === SurchargeType.Service && totalSurcharge.total === 0) {
            displayServiceFeeWithZeroPrice = true;
          }
          feeTotal += totalSurcharge.total;
        }
      }
    });
    this.cartSalespointFees = spFeeTotal > 0 ? [{name: [{language: 'en', value: 'general.api_fees_salespoint'}], total: spFeeTotal, quantity: spFees.length}] : [];
    this.cartFeesCompact = feeTotal > 0 || displayServiceFeeWithZeroPrice ? {name: [{language: 'en', value: (this.isSelfService ? 'general.api_fees_combined' : 'general.api_fees_extra')}], total: feeTotal, quantity: this.cartFees.length} : null;
    this.cartTotalPrice = (this.cart?.total?.ticketTotal + sumBy(this.cartHiddenFees, 'total')) || 0;
    this.cartSubTotal = this.totalSum - spFeeTotal;
  }

  public get hasMultipleFees(): boolean {
    return this.cartFees.length > 1;
  }

  public get cartSurcharges(): CartTotalSurcharge[] {
    return this.cart?.total?.surcharges || [];
  }

  public get cartGiftCardCreditTotal(): number {
    return this.cart?.total?.giftCardCreditTotal || 0;
  }

  public get cartTotalDiscount(): number {
    return this.cart?.total?.discountAmount || 0;
  }

  public get cartTotalDelivery(): DeliveryType {
    return this.cart?.total?.legacyDelivery;
  }

  public get isEmpty(): boolean {
    return !this.events.length;
  }
  public get hasSelectedTickets(): boolean {
    return this.selectionMap.checked !== ChildSelectionState.NONE;
  }
  public get hasMoreThanOneTicket(): boolean {
    return this.ticketCount > 1;
  }
  public get areAllTicketsSelected(): boolean {
    return this.selectionMap.checked === ChildSelectionState.ALL;
  }

  public clearBooking(): void {
    this.currentBooking = null;
    this.storageService.remove(CartService.STORAGE_CART_LAST_BOOKING);
  }

  public updateTicketCustomPrice(eventId: number, ticket: CustomTicket): void {
    this.customPriceRequest = [{id: ticket.id, price: this.priceInCentsPipe.transform(+ticket.customPrice)}];
  }
  public updateTicketPriceType(eventId: number, ticket: LegacyTicket): void {
    this.priceTypeRequest = [{id: ticket.id, priceTypeId: ticket.priceTypeId}];
  }

  protected eventPriceTypeTicketsIds(eventId: number, priceClassId: number, priceTypeId: number, row: string = null): number[] {
    const cartEvent: CartEvent = this.events.find(event => event.id === eventId);
    return cartEvent?.legacyTickets?.filter(ticket =>
      ticket.priceClassId === priceClassId && ticket.priceTypeId === priceTypeId && (row == null || ticket.row === row)).map(t => t.id) || [];
  }

  protected setSubTotals(total: CartTotal): void {
    this.cartTotal = total;
    this.ticketTypeChanges = {};
    this.priceTypeChanges = {};
    this.events.forEach(event => {
      event.legacyTickets.forEach((ticket: CustomTicket) => {
        ticket.customPrice = this.ticketPricePipe.transform(
          ticket,
          !this.centrePropertyService.isEnabled('exclude_own_fees') || event.ownerSuperCentreId === this.authService.user.superCentreId,
          true);
      })
    });
  }

  public get giftCodes(): string[] {
    let codes: string[] = [];
    this._cart.giftCards?.forEach(card => {
      if (card.codes.length) {
        codes = codes.concat(card.codes.map(gift => gift.code));
      }
    });
    return codes;
  }

  protected updateTickets(): void {
    this.ticketTypeChanges = {};
    this.groupPriceTypeMap = {};
    this.groupCustomPriceMap = {};
    this.selectAllTickets();
  }

  protected mapTickets(inEventList: CartEvent[] = []): void {
    const eventList: CartEvent[] = cloneDeep(inEventList);
    this.isLoading = true;
    let currentSortedEvents: CartSortedEvent[] = [];
    const eventTicketIdMap: Record<string, number[]> = {};
    this.ticketCount = 0;
    this.cartEventIds = [];
    this.cartDraftEvent = null;
    currentSortedEvents = eventList.map((event, eventIndex): CartSortedEvent => {
      const currentEvent: CartSortedEvent = event as CartSortedEvent;
      currentEvent.quantity = 0;
      currentEvent.total = 0;
      currentEvent.availablePriceMap = {};
      currentEvent.surcharges = [];
      this.cartEventIds.push(event.id);
      if (eventIndex === 0) {
        this.eventService.getEvent(inEventList[0].id).subscribe((event) => {
          this.cartDraftEvent = {id: event.eventId, isDraft: this.eventService.isDraft(event.status.id), boEvent: event.boEvent}
        })
      }
      if (event.boEvent) {
        this.cartType = CartTypes.SelfService;
        event.unassignedTickets.forEach((type: UnassignedTicket) => {
          currentEvent.quantity = currentEvent.quantity + type.quantity;
          currentEvent.total = currentEvent.total + (type.quantity * type.price);
          type.surcharges?.forEach(ticketSurcharge => {
            const eventSurcharge: CartTotalSurcharge = currentEvent.surcharges
              .find(s => s.id === ticketSurcharge.id && s.type === ticketSurcharge.type);
            if (eventSurcharge) {
              eventSurcharge.quantity++;
              eventSurcharge.total = eventSurcharge.total + ticketSurcharge.price;
            } else {
              currentEvent.surcharges.push({
                id: ticketSurcharge.id,
                type: ticketSurcharge.type,
                name: ticketSurcharge.name,
                ownerSuperCentreId: event.ownerSuperCentreId,
                total: ticketSurcharge.price,
                quantity: 1
              });
            }
          });
        })
      } else {
        this.cartType = CartTypes.Legacy;
        currentEvent.legacyTypes = [];
        currentEvent.legacyZones = [];
        currentEvent.legacyRows = [];
        currentEvent.legacyTickets.forEach(ticket => {
          currentEvent.quantity++;
          currentEvent.total = currentEvent.total + ticket.price;
          this.mapTicketId(ticket.id, event.id, eventTicketIdMap);
          if (!currentEvent.legacyTypes.some(type => type.id === ticket.priceTypeId)) {
            const newLegacyType: CartGroup = {
              id: ticket.priceTypeId,
              name: ticket.priceTypeName,
              quantity: 0,
              tickets: event.legacyTickets.filter(t => t.priceTypeId === ticket.priceTypeId)
            };
            newLegacyType.quantity = newLegacyType.tickets.length;
            currentEvent.legacyTypes.push(newLegacyType);
          }
          if (!currentEvent.legacyZones.some(priceZone => priceZone.id === ticket.priceClassId && priceZone.typeId === ticket.priceTypeId)) {
            const newLegacyZone: CartSectorGroup = {
              id: ticket.priceClassId,
              typeId: ticket.priceTypeId,
              typeName: ticket.priceTypeName,
              name: ticket.priceClassName,
              quantity: 0,
              tickets: event.legacyTickets.filter(t => t.priceClassId === ticket.priceClassId && t.priceTypeId === ticket.priceTypeId)
            };
            newLegacyZone.quantity = newLegacyZone.tickets.length;
            currentEvent.legacyZones.push(newLegacyZone);
          }
          if (!currentEvent.legacyRows.some(priceRow => priceRow.id === ticket.priceTypeId && priceRow.row === ticket.row)) {
            const newLegacyRow: CartRowGroup = {
              id: ticket.priceTypeId,
              row: ticket.row,
              name: ticket.priceTypeName,
              quantity: 0,
              tickets: event.legacyTickets.filter(t => t.priceTypeId === ticket.priceTypeId && t.row === ticket.row)
            };
            newLegacyRow.quantity = newLegacyRow.tickets.length;
            currentEvent.legacyRows.push(newLegacyRow);
          }
          const priceMapKey: string = ticket.sectorId + '_' + ticket.priceClassId;
          if (!currentEvent.availablePriceMap[priceMapKey]) {
            currentEvent.availablePriceMap[priceMapKey] = currentEvent.legacyPrices.find(priceGroup =>
              priceGroup.sectorId === ticket.sectorId && priceGroup.priceClassId === ticket.priceClassId)?.priceTypes || [];
          }
          ticket.customPrice = this.ticketPricePipe.transform(
            ticket,
            !this.centrePropertyService.isEnabled('exclude_own_fees') || event.ownerSuperCentreId === this.authService.user.superCentreId,
            true);
        });
      }
      this.ticketCount = this.ticketCount + currentEvent.quantity;
      return currentEvent;
    });
    this.ticketEventMap = currentSortedEvents;
    this.eventTicketIdMap = eventTicketIdMap;
    this.isLoading = false;
  }
  private mapTicketId(ticketId: number, eventId: number, ticketIdMap: Record<string, number[]>): void {
    if (!ticketIdMap[eventId]) {
      ticketIdMap[eventId] = [ticketId];
    } else {
      ticketIdMap[eventId].push(ticketId);
    }
  }

  protected get getSelectedEventIds(): number[] {
    const selectedEventIds: number[] = [];
    Object.keys(this.selectionMap.events).forEach(eventId => {
      if (this.selectionMap.events[eventId].checked !== ChildSelectionState.NONE) {
        selectedEventIds.push(+eventId);
      }
    });
    return selectedEventIds;
  }

  public updateClientNotificationEmail(newValue: string = ''): void {
    this.clientNotificationEmail = newValue;
    this.storageService.set(CartService.STORAGE_CART_CLIENT_NOTIFICATION_EMAIL, this.clientNotificationEmail);
  }

  public populateSelectionMap(selectionState: ChildSelectionState = ChildSelectionState.ALL, keepState?: boolean, updateSubTotals?: boolean): void {
    const newSelectionMap: SelectionMap = {checked: selectionState, events: {}};
    this.ticketEventMap.forEach(event => {
      this.populateSelectionMapEvent(event, newSelectionMap, selectionState, keepState);
    });
    this.selectionMap = newSelectionMap;
    this.updateSelectionMapStates(this.selectionMap, updateSubTotals);
  }
  public populateSelectionMapEvent(event: CartSortedEvent,
                                   selectionMap: SelectionMap,
                                   selectionState: ChildSelectionState,
                                   keepState?: boolean): void {
    selectionMap.events[event.id] = {
      checked: selectionState,
      tickets: {},
      ticketTypes: {},
      priceTypes: {},
      priceRows: {},
      priceZones: {}
    };
    event.legacyTickets?.forEach(ticket => {
      selectionMap.events[event.id].tickets[ticket.id] = selectionState;
      if (keepState) {
        selectionMap.events[event.id].tickets[ticket.id] = this.selectionMap?.events[event.id]?.tickets[ticket.id] || selectionState;
      }
      this.populateSelectionMapPriceZone(event.id, ticket.priceClassId + '_' + ticket.priceTypeId, selectionMap, selectionState, keepState, ticket.id);
      this.populateSelectionMapPriceRow(event.id, ticket.row + '_' + ticket.priceTypeId, selectionMap, selectionState, keepState, ticket.id);
      this.populateSelectionMapPriceType(event.id, ticket.priceTypeId, selectionMap, selectionState, keepState, ticket.id);
      if (!this.groupPriceTypeMap[ticket.priceClassId + '_' + ticket.priceTypeId]) {
        this.groupPriceTypeMap[ticket.priceClassId + '_' + ticket.priceTypeId] = ticket.priceTypeId;
      }
      if (!this.groupPriceTypeMap[ticket.row + '_' + ticket.priceTypeId]) {
        this.groupPriceTypeMap[ticket.row + '_' + ticket.priceTypeId] = ticket.priceTypeId;
      }
      if (!this.groupPriceTypeMap[ticket.priceTypeId]) {
        this.groupPriceTypeMap[ticket.priceTypeId] = ticket.priceTypeId;
      }
    })
    event.unassignedTickets?.forEach(ticketType => {
      this.populateSelectionMapTicketType(event.id, ticketType.ticketTypeUuid, selectionMap, selectionState, keepState);
    })
  }
  public populateSelectionMapPriceZone(eventId: number,
                                       idString: string,
                                       selectionMap: SelectionMap,
                                       selectionState: ChildSelectionState,
                                       keepState?: boolean,
                                       ticketId?: number): void {
    selectionMap.events[eventId].priceZones[idString] = {
      checked: selectionState,
      tickets: selectionMap.events[eventId].priceZones[idString]?.tickets || {}
    };
    if (ticketId) {
      selectionMap.events[eventId].priceZones[idString].tickets[ticketId] = selectionState;
      if (keepState) {
        selectionMap.events[eventId].priceZones[idString].tickets[ticketId] = this.selectionMap?.events[eventId]?.priceZones[idString]?.tickets[ticketId] || selectionState;
      }
    } else {
      Object.keys(selectionMap.events[eventId].priceZones[idString].tickets).forEach(ticketKey => {
        selectionMap.events[eventId].priceZones[idString].tickets[ticketKey] = selectionState;
        if (keepState) {
          selectionMap.events[eventId].priceZones[idString].tickets[ticketKey] = this.selectionMap?.events[eventId]?.priceZones[idString]?.tickets[ticketKey] || selectionState;
        }
        selectionMap.events[eventId].tickets[ticketKey] = selectionState;
        if (keepState) {
          selectionMap.events[eventId].tickets[ticketKey] = this.selectionMap?.events[eventId]?.tickets[ticketKey] || selectionState;
        }
      });
    }
  }
  public populateSelectionMapPriceRow(eventId: number,
                                      idString: string,
                                      selectionMap: SelectionMap,
                                      selectionState: ChildSelectionState,
                                      keepState?: boolean,
                                      ticketId?: number): void {
    selectionMap.events[eventId].priceRows[idString] = {
      checked: selectionState,
      tickets: selectionMap.events[eventId].priceRows[idString]?.tickets || {}
    };
    if (ticketId) {
      selectionMap.events[eventId].priceRows[idString].tickets[ticketId] = selectionState;
      if (keepState) {
        selectionMap.events[eventId].priceRows[idString].tickets[ticketId] = this.selectionMap?.events[eventId]?.priceRows[idString]?.tickets[ticketId] || selectionState;
      }
    } else {
      Object.keys(selectionMap.events[eventId].priceRows[idString].tickets).forEach(ticketKey => {
        selectionMap.events[eventId].priceRows[idString].tickets[ticketKey] = selectionState;
        if (keepState) {
          selectionMap.events[eventId].priceRows[idString].tickets[ticketKey] = this.selectionMap?.events[eventId]?.priceRows[idString]?.tickets[ticketKey] || selectionState;
        }
        selectionMap.events[eventId].tickets[ticketKey] = selectionState;
        if (keepState) {
          selectionMap.events[eventId].tickets[ticketKey] = this.selectionMap?.events[eventId]?.tickets[ticketKey] || selectionState;
        }
      });
    }
  }
  public populateSelectionMapPriceType(eventId: number,
                                       typeId: number,
                                       selectionMap: SelectionMap,
                                       selectionState: ChildSelectionState,
                                       keepState?: boolean,
                                       ticketId?: number): void {
    selectionMap.events[eventId].priceTypes[typeId] = {
      checked: selectionState,
      tickets: selectionMap.events[eventId].priceTypes[typeId]?.tickets || {}
    };
    if (ticketId) {
      selectionMap.events[eventId].priceTypes[typeId].tickets[ticketId] = selectionState;
      if (keepState) {
        selectionMap.events[eventId].priceTypes[typeId].tickets[ticketId] = this.selectionMap?.events[eventId]?.priceTypes[typeId]?.tickets[ticketId] || selectionState;
      }
    } else {
      Object.keys(selectionMap.events[eventId].priceTypes[typeId].tickets).forEach(ticketKey => {
        selectionMap.events[eventId].priceTypes[typeId].tickets[ticketKey] = selectionState;
        if (keepState) {
          selectionMap.events[eventId].priceTypes[typeId].tickets[ticketKey] = this.selectionMap?.events[eventId]?.priceTypes[typeId]?.tickets[ticketKey] || selectionState;
        }
        selectionMap.events[eventId].tickets[ticketKey] = selectionState;
        if (keepState) {
          selectionMap.events[eventId].tickets[ticketKey] = this.selectionMap?.events[eventId]?.tickets[ticketKey] || selectionState;
        }
      });
    }
  }
  public populateSelectionMapTicketType(eventId: number,
                                        typeId: string,
                                        selectionMap: SelectionMap,
                                        selectionState: ChildSelectionState,
                                        keepState?: boolean): void {
    selectionMap.events[eventId].ticketTypes[typeId] = selectionState;
    if (keepState) {
      selectionMap.events[eventId].ticketTypes[typeId] = this.selectionMap?.events[eventId]?.ticketTypes[typeId] || selectionState;
    }
  }

  public updateSelectionMapStates(selectionMap: SelectionMap, updateSubTotals?: boolean): void {
    Object.keys(selectionMap.events).forEach(eventId => {
      Object.keys(selectionMap.events[eventId].priceTypes).forEach(priceTypeId => {
        selectionMap.events[eventId].priceTypes[priceTypeId].checked = this.getSelectionStateFromGroup(selectionMap.events[eventId].priceTypes[priceTypeId].tickets);
      });
      Object.keys(selectionMap.events[eventId].priceZones).forEach(priceZoneId => {
        selectionMap.events[eventId].priceZones[priceZoneId].checked = this.getSelectionStateFromGroup(selectionMap.events[eventId].priceZones[priceZoneId].tickets);
      });
      Object.keys(selectionMap.events[eventId].priceRows).forEach(priceRowId => {
        selectionMap.events[eventId].priceRows[priceRowId].checked = this.getSelectionStateFromGroup(selectionMap.events[eventId].priceRows[priceRowId].tickets);
      });
      if (Object.keys(selectionMap.events[eventId].tickets).length) {
        selectionMap.events[eventId].checked = this.getSelectionStateFromGroup(selectionMap.events[eventId].tickets);
      } else {
        selectionMap.events[eventId].checked = this.getSelectionStateFromGroup(selectionMap.events[eventId].ticketTypes);
      }
    });
    if (Object.values(selectionMap.events).length && Object.values(selectionMap.events).every(v => v.checked === ChildSelectionState.ALL)) {
      selectionMap.checked = ChildSelectionState.ALL;
    } else if (Object.values(selectionMap.events).some(v => v.checked !== ChildSelectionState.NONE)) {
      selectionMap.checked = ChildSelectionState.SOME;
    } else {
      selectionMap.checked = ChildSelectionState.NONE;
    }
    if (updateSubTotals) {
      this.updateSubTotals();
    }
  }
  private getSelectionStateFromGroup(selectionGroup: Record<string, ChildSelectionState>): ChildSelectionState {
    if (Object.values(selectionGroup).every(v => v === ChildSelectionState.ALL)) {
      return ChildSelectionState.ALL;
    } else if (Object.values(selectionGroup).some(v => v === ChildSelectionState.ALL)) {
      return ChildSelectionState.SOME;
    }
    return ChildSelectionState.NONE;
  }

  public get getSelectedTicketIds(): number[] {
    const selectedTicketIds: number[] = [];
    Object.keys(this.selectionMap.events).forEach(eventId => {
      Object.keys(this.selectionMap.events[eventId].tickets).forEach(ticketId => {
        if (this.selectionMap.events[eventId].tickets[ticketId] === ChildSelectionState.ALL) {
          selectedTicketIds.push(+ticketId);
        }
      })
    });
    return selectedTicketIds;
  }

  public hasSelfServicePromocode(eventId: number): boolean {
    return this.events.some(event => event.id === eventId && !!event.promocode);
  }

  public updateTotalScrapSum(): void {
    this.totalScrapSum = 0;
    this.events.forEach(event => {
      event.legacyTickets.forEach(ticket => {
        if (this.selectionMap.events[event.id].tickets[ticket.id] === ChildSelectionState.ALL) {
          this.totalScrapSum = this.totalScrapSum + (ticket.price + sumBy(ticket.surcharges, 'total'));
        }
      })
    });
  }
}
